import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function CheckerProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(location.state?.item || location.state?.product || null);
    const [loading, setLoading] = useState(!product);
    const [error, setError] = useState(null);

    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/products/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success || resData.data) {
                const fetchedData = resData.data;
                // Handle case where API response returns an array inside resData.data
                if (Array.isArray(fetchedData)) {
                    setProduct(fetchedData[0] || null);
                } else {
                    setProduct(fetchedData || resData);
                }
            } else {
                setError(resData.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching checker product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
    }, [id, product, fetchProductById]);

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate(-1)}
                        >
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("checker product details")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(-1);
                                            }}
                                            className="mc-breadcrumb-link"
                                        >
                                            {t("checker products")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {t("product details")}
                                    </li>
                                </ul>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(-1)}
                            >
                                {t("back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                {/* Left Column: Media Section */}
                <Col lg={4}>
                    <Card className="mb-4">
                        <Card.Body className="text-center">
                            {/* Primary Product Image */}
                            <div className="mb-3">
                                {product.first_img ? (
                                    <img
                                        src={`${process.env.REACT_APP_IMG_URL}${product.first_img}`}
                                        alt={product.title}
                                        className="img-fluid rounded border"
                                        style={{ maxHeight: "220px", objectFit: "contain", width: "100%" }}
                                    />
                                ) : (
                                    <div
                                        className="bg-light d-flex align-items-center justify-content-center rounded"
                                        style={{ height: "180px" }}
                                    >
                                        <span className="text-muted">{t("no image")}</span>
                                    </div>
                                )}
                            </div>

                            {/* Title & Badge */}
                            <h5 className="mt-2 mb-1">{product.title || "N/A"}</h5>
                            <Badge bg="primary" className="mb-3">
                                {product.product_code || "N/A"}
                            </Badge>

                            {/* Card Image Display */}
                            {product.card_img && (
                                <div className="border-top pt-3 mt-2 text-start">
                                    <small className="text-muted fw-bold d-block mb-2">{t("card image")}:</small>
                                    <div className="text-center">
                                        <img
                                            src={`${process.env.REACT_APP_IMG_URL}${product.card_img}`}
                                            alt="Card Preview"
                                            className="img-fluid rounded border"
                                            style={{ maxHeight: "180px", objectFit: "contain", width: "100%" }}
                                        />
                                    </div>
                                </div>
                            )}

                            {product.rejection_reason && (
                                <div className="alert alert-danger py-2 px-3 my-2 text-start">
                                    <strong>{t("rejection reason")}: </strong>
                                    <span>{product.rejection_reason}</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Gallery Images */}
                    {product.images && product.images.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0">{t("gallery images")}</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    {product.images.map((imgObj, idx) => (
                                        <Col key={imgObj.id || idx} xs={6}>
                                            <img
                                                src={`${process.env.REACT_APP_IMG_URL}${imgObj.image}`}
                                                alt={`Gallery ${idx + 1}`}
                                                className="img-fluid rounded border"
                                                style={{ height: "100px", width: "100%", objectFit: "cover" }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col lg={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("product information")}</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col sm={6}>
                                    <strong>{t("category")}: </strong>
                                    <span>
                                        {typeof product.category === "object"
                                            ? product.category?.name
                                            : product.category || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("brand")}: </strong>
                                    <span>
                                        {typeof product.brand === "object"
                                            ? product.brand?.name
                                            : product.brand || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("selling style")}: </strong>
                                    <span>{product.selling_style || "N/A"}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("quantity")}: </strong>
                                    <span>{product.product_qty ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("Buying Price")}: </strong>
                                    <span>${product.buying_price ?? product.price ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("sell price dollar")}: </strong>
                                    <span>${product.sell_price_dollar ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("shipping charges dollar")}: </strong>
                                    <span>${product.shipping_charges_dollar ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("product condition")}: </strong>
                                    <span>{product.product_condition || "N/A"}</span>
                                </Col>

                                <Col sm={12}>
                                    <strong>{t("short description")}: </strong>
                                    <p className="text-muted mb-0">{product.short_description || "N/A"}</p>
                                </Col>

                                <Col sm={12}>
                                    <strong>{t("description")}: </strong>
                                    {product.description ? (
                                        <div
                                            className="text-muted mb-0"
                                            dangerouslySetInnerHTML={{
                                                __html: new DOMParser().parseFromString(product.description, "text/html").body.textContent || ""
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted mb-0">N/A</p>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}